import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Follow } from '@app/models/follow';
import { ApiService } from '@app/services/api/api.service';
import { FollowService } from '@app/services/api/follow.service';
import { FlashService } from '@app/services/flash.service';
import {
  ChangeClpUsd,
  ChangeClpUsdFail,
  ChangeClpUsdSuccess,
  ChangeNameFollow,
  ChangeNameFollowFail,
  CreateSetting,
  CreateSettingFail,
  CreateSettingSuccess,
  DeleteBanner,
  DeleteBannerFail,
  Destroy,
  DestroyFail,
  DestroySuccess,
  FollowActionTypes,
  FollowsCancel,
  FollowsCancelFail,
  FollowsCancelSuccess,
  FollowsChargeComplete,
  FollowsChargeCompleteFail,
  FollowsChargeCompleteSuccess,
  FollowsCountryBuyer,
  FollowsCountryBuyerFail,
  FollowsCountryBuyerSuccess,
  FollowsNoMembersSuccess,
  FollowsTableCountryBuyer,
  FollowsTableCountryBuyerFail,
  FollowsTableCountryBuyerSuccess,
  GetBannerLink,
  GetBannerLinkFail,
  GetBannerShowroomFood,
  GetBannerShowroomFoodFail,
  GetBannerShowroomFoodSuccess,
  GetFollowYears,
  GetFollowYearsFail,
  GetFollowYearsSuccess,
  GetSummaryData,
  GetSummaryDataFail,
  GetSummaryDataPending,
  Load,
  LoadFail,
  LoadSingle,
  LoadSingleFail,
  LoadSingleSuccess,
  LoadSuccess,
  RemoveOne,
  Search,
  SearchFail,
  SearchFollow,
  SearchFollowFail,
  SearchFollowSuccess,
  SearchSuccess,
  Update,
  UpdateFail,
  UpdateOne,
  UpdateSuccess,
  UploadBanner,
  UploadBannerFail,
  UpsertSetting
} from '@core/actions/follow.actions';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, exhaustMap, map, switchMap, tap } from 'rxjs/operators';

@Injectable()
export class FollowEffects {
  @Effect()
  getSummaryData$ = this.actions$.pipe(
    ofType<GetSummaryData>(FollowActionTypes.GetSummaryData),
    map(action => [action.FollowId, action.data]),
    exhaustMap(([followId, data]: [any, any]) => {
      return this.followsApi.getSummaryData(followId, data).pipe(
        concatMap(response => {
          return [new GetSummaryDataPending(response)];
        }),
        catchError(e => {
          return of(new GetSummaryDataFail(e));
        })
      );
    })
  );

  @Effect({ dispatch: false })
  GetSummaryDataFail$: Observable<Action> = this.actions$.pipe(
    ofType<GetSummaryDataFail>(FollowActionTypes.GetSummaryDataFail),
    map(action => action.error),
    tap(res =>
      this.fs.emit({
        message: 'Error al traer resumen',
        type: 'danger'
      })
    )
  );

  @Effect()
  deleteBanner$ = this.actions$.pipe(
    ofType<DeleteBanner>(FollowActionTypes.DeleteBanner),
    map(action => [action.followId, action.position]),
    exhaustMap(([followId, position]: [any, any]) => {
      return this.followsApi.deleteBanner(followId, position).pipe(
        concatMap(response => {
          return [new GetBannerShowroomFood(followId)];
        }),
        catchError(e => {
          return of(new DeleteBannerFail(e));
        })
      );
    })
  );

  @Effect()
  uploadBanner$ = this.actions$.pipe(
    ofType<UploadBanner>(FollowActionTypes.UploadBanner),
    map(action => [action.link, action.file, action.followId]),
    exhaustMap(([link, file, followId]: [any, any, any]) => {
      const url = link.signedUrl;
      const fileId = link.id;
      return this.api.uploadFile(url, file).pipe(
        concatMap(response => {
          return [new GetBannerShowroomFood(followId)];
        }),
        catchError(e => {
          return of(new UploadBannerFail(e));
        })
      );
    })
  );

  @Effect()
  getFollowYears$ = this.actions$.pipe(
    ofType<GetFollowYears>(FollowActionTypes.GetFollowYears),
    map(action => []),
    exhaustMap(() => {
      return this.api.getFollowYears().pipe(
        concatMap(response => {
          return [new GetFollowYearsSuccess(response)];
        }),
        catchError(e => {
          return of(new GetFollowYearsFail(e));
        })
      );
    })
  );

  @Effect()
  getBannerLink$ = this.actions$.pipe(
    ofType<GetBannerLink>(FollowActionTypes.GetBannerLink),
    map(action => [action.followId, action.file, action.fileableType]),
    exhaustMap(([followId, file, fileableType]) =>
      this.api.getBannerUploadLink(followId, file, fileableType).pipe(
        concatMap((link: any) => {
          return [new UploadBanner(link, file, followId)];
        }),
        catchError(error => of(new GetBannerLinkFail(error)))
      )
    )
  );

  @Effect()
  changeNameFollow$ = this.actions$.pipe(
    ofType<ChangeNameFollow>(FollowActionTypes.ChangeNameFollow),
    map(action => [action.name, action.followId]),
    exhaustMap(([name, followId]) =>
      this.api.changeNameFollow(name, followId).pipe(
        concatMap((link: any) => {
          return [new Load()];
        }),
        catchError(error => of(new ChangeNameFollowFail(error)))
      )
    )
  );

  @Effect()
  getBannerShowroomFood$: Observable<Action> = this.actions$.pipe(
    ofType<GetBannerShowroomFood>(FollowActionTypes.GetBannerShowroomFood),
    map(action => action.followId),
    switchMap(followId => {
      return this.followsApi.getBanners(followId).pipe(
        map((data: any) => new GetBannerShowroomFoodSuccess(data)),
        catchError(error => of(new GetBannerShowroomFoodFail(error)))
      );
    })
  );

  @Effect()
  load$: Observable<Action> = this.actions$.pipe(
    ofType<Load>(FollowActionTypes.Load),
    map(action => action.options),
    switchMap(options => {
      return this.followsApi.get(options).pipe(
        map((follows: Follow[]) => new LoadSuccess(follows)),
        catchError(error => of(new LoadFail(error)))
      );
    })
  );

  @Effect()
  loadOne$: Observable<Action> = this.actions$.pipe(
    ofType<LoadSingle>(FollowActionTypes.LoadSingle),
    map(action => action.id),
    switchMap(id => {
      return this.followsApi.getOne(id).pipe(
        map((follow: Follow) => new LoadSingleSuccess(follow)),
        catchError(error => of(new LoadSingleFail(error)))
      );
    })
  );
  @Effect()
  createSetting$: Observable<Action> = this.actions$.pipe(
    ofType<CreateSetting>(FollowActionTypes.CreateSetting),
    map(action => [action.followId, action.data]),
    switchMap(([followId, data]) => {
      return this.api.createFollowSetting(followId, data).pipe(
        map((setting: any) => new CreateSettingSuccess(followId, setting)),
        catchError(error => of(new CreateSettingFail(error)))
      );
    })
  );
  @Effect()
  createSettingSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<CreateSettingSuccess>(FollowActionTypes.CreateSettingSuccess),
    map(action => new UpsertSetting(action.followId, action.setting))
  );

  @Effect()
  destroy$: Observable<Action> = this.actions$.pipe(
    ofType<Destroy>(FollowActionTypes.Destroy),
    map(action => action.id),
    exhaustMap(id => {
      return this.followsApi.delete(id).pipe(
        map((setting: any) => new DestroySuccess(id)),
        catchError(error => of(new DestroyFail(error)))
      );
    })
  );

  @Effect()
  followsNoMembers$: Observable<Action> = this.actions$.pipe(
    ofType<Destroy>(FollowActionTypes.FollowsNoMembers),
    map(action => action),
    exhaustMap(id => {
      return this.api.getFollowsNoMembers().pipe(
        map((data: any) => {
          return new FollowsNoMembersSuccess(data);
        }),
        catchError(error => of(new DestroyFail(error)))
      );
    })
  );

  @Effect()
  followsTableCountryBuyer$: Observable<Action> = this.actions$.pipe(
    ofType<FollowsTableCountryBuyer>(FollowActionTypes.FollowsTableCountryBuyer),
    map(action => action),
    exhaustMap(follow => {
      return this.followsApi.getTableCountryBuyer(follow.followId).pipe(
        map((data: any) => {
          return new FollowsTableCountryBuyerSuccess(data);
        }),
        catchError(error => of(new FollowsTableCountryBuyerFail(error)))
      );
    })
  );

  @Effect()
  followsCountryBuyer$: Observable<Action> = this.actions$.pipe(
    ofType<FollowsCountryBuyer>(FollowActionTypes.FollowsCountryBuyer),
    map(action => action),
    exhaustMap(data => {
      return this.followsApi.getCountryBuyerCheck(
        data.followId,
        data.countryId,
        data.userId
      ).pipe(
        map((data: any) => {
          return new FollowsCountryBuyerSuccess(data);
        }),
        catchError(error => of(new FollowsCountryBuyerFail(error)))
      );
    })
  );

  @Effect()
  followsChargeComplete$: Observable<Action> = this.actions$.pipe(
    ofType<FollowsChargeComplete>(FollowActionTypes.FollowsChargeComplete),
    map(action => action),
    exhaustMap(data => {
      return this.followsApi.addChargeComplete(data.followId, data.countryId, data.userId).pipe(
        concatMap((dataTwo: any) => {
          return [new FollowsChargeCompleteSuccess(dataTwo), new FollowsCountryBuyer(data.followId, data.countryId, data.userId)];
        }),
        catchError(error => of(new FollowsChargeCompleteFail(error)))
      );
    })
  );

  @Effect({ dispatch: false})
  followsChargeCompleteSuccess$: Observable<void> = this.actions$.pipe(
    ofType<FollowsChargeCompleteSuccess>(FollowActionTypes.FollowsChargeCompleteSuccess),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Se indico que la carga esta completa.' });
    })
  );

  
  @Effect()
  destroySuccess$: Observable<Action> = this.actions$.pipe(
    ofType<DestroySuccess>(FollowActionTypes.DestroySuccess),
    map(action => {
      this.router.navigate(['/follows']);
      return new RemoveOne(action.id);
    })
  );

  @Effect()
  update$: Observable<Action> = this.actions$.pipe(
    ofType<Update>(FollowActionTypes.Update),
    map(action => [action.followId, action.data]),
    switchMap(([followId, data]) => {
      return this.followsApi.update(followId, data).pipe(
        map((follow: Follow) => new UpdateSuccess(followId, follow)),
        catchError(error => of(new UpdateFail(error)))
      );
    })
  );

  @Effect()
  cancelFollow$: Observable<Action> = this.actions$.pipe(
    ofType<FollowsCancel>(FollowActionTypes.FollowsCancel),
    map(action => action.followId),
    switchMap((followId) => {
      return this.followsApi.cancelFollow(followId).pipe(
        map((follow: any) => new FollowsCancelSuccess(follow)),
        catchError(error => of(new FollowsCancelFail(error)))
      );
    })
  );

  @Effect({dispatch: false})
  cancelFollowFail$: Observable<void> = this.actions$.pipe(
    ofType<FollowsCancelFail>(FollowActionTypes.FollowsCancelFail),
    map(action => {
      this.fs.emit({ type: 'danger', message: 'Este follow ya fue cancelado' });
    })
  );

  @Effect({dispatch: false})
  cancelFollowSuccess: Observable<void> = this.actions$.pipe(
    ofType<FollowsCancelSuccess>(FollowActionTypes.FollowsCancelSuccess),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Follow actualizado' });
    })
  );
  @Effect()
  updateSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<UpdateSuccess>(FollowActionTypes.UpdateSuccess),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Follow actualizado' });
      return new UpdateOne(action.data);
    })
  );
  @Effect()
  changeClpUsd$: Observable<Action> = this.actions$.pipe(
    ofType<ChangeClpUsd>(FollowActionTypes.ChangeClpUsd),
    map(action => [action.value, action.followId]),
    switchMap(([value, followId]) => {
      return this.followsApi.changeClpUsd(value, followId).pipe(
        concatMap(response => {
          return [new ChangeClpUsdSuccess()];
        }),
        catchError(e => {
          return of(new ChangeClpUsdFail(e));
        })
      )
      
    })
  );

  @Effect()
  changeClpUsdSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<ChangeClpUsdSuccess>(FollowActionTypes.ChangeClpUsdSuccess),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Follow actualizado' });
      return new ChangeClpUsdFail("error")
    })
  );

  @Effect()
  UpdateOne$: Observable<Action> = this.actions$.pipe(
    ofType<UpdateOne>(FollowActionTypes.UpdateOne),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Follow actualizado' });
      return new Load();
    })
  );

  @Effect()
  search: Observable<Action> = this.actions$.pipe(
    ofType<Search>(FollowActionTypes.Search),
    map(action => [action.conditions]),
    switchMap(([conditions, FollowId]) =>
      this.followsApi.search(conditions).pipe(
        map((followIds: number[]) => new SearchSuccess(followIds)),
        catchError(error => of(new SearchFail(error)))
      )
    )
  );

  @Effect()
  searchFollow: Observable<Action> = this.actions$.pipe(
    ofType<SearchFollow>(FollowActionTypes.SearchFollow),
    map(action => [action.conditions]),
    switchMap(([conditions]) =>
      this.followsApi.searchFollow(conditions).pipe(
        map((Follows: any) => new SearchFollowSuccess(Follows)),
        catchError(error => of(new SearchFollowFail(error)))
      )
    )
  );

  constructor(
    private actions$: Actions,
    private api: ApiService,
    private followsApi: FollowService,
    private fs: FlashService,
    private router: Router
  ) {}
}
